"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMarketPriceOptional } from "../context/MarketPriceContext";
import { sourceUrl, marketPageUrl, type Source } from "@/lib/source-url";
import { ChevronLeftIcon } from "@/app/components/icons/ChevronLeftIcon";
import { useT } from "@/hooks/useT";

interface MarketHeroProps {
  marketData: {
    slug: string;
    question: string;
    image: string | null;
    endDate: string | null;
    closed: boolean;
    active: boolean;
    acceptingOrders: boolean;
    outcomes: string[];
    outcomePrices: number[];
    volume: number;
    volume24hr: number;
    liquidity: number;
    oneDayPriceChange: number | null;
    parentEventSlug?: string | null;
    competitive: number | null;
    openInterest?: number;
    conditionId?: string | null;
    eventCategory?: string | null;
  };
  source: "kalshi" | "polymarket" | "predict";
  isKalshi?: boolean;
  umaDispute?: { state: string } | null;
  showClosedBanner?: boolean;
  /** Hide the Vol / 24h / Liq chip row — set on short-lived crypto updown
   * events where the numbers are in the single-digit dollar range and
   * read as noise. */
  hideStats?: boolean;
}

const SOURCE_CONFIG: Record<string, { color: string; icon: string; label: string }> = {
  predict: { color: "#6366F1", icon: "", label: "Predict.fun" },
  kalshi: { color: "#00D26A", icon: "/logos/kalshi/k-icon.svg", label: "Kalshi" },
  polymarket: { color: "#0066FF", icon: "/logos/polymarket/icon-white.png", label: "Polymarket" },
};

function getExternalUrl(source: "kalshi" | "polymarket" | "predict", marketData: MarketHeroProps["marketData"]): string {
  if (source === "polymarket") {
    return `https://polymarket.com/event/${marketData.parentEventSlug || marketData.slug}`;
  }

  // Prefer the unified event route when we know the parent event; marketPageUrl
  // falls back to /<source>/market/<slug> if no event slug is available.
  return marketPageUrl({
    marketSlug: marketData.slug,
    eventSlug: marketData.parentEventSlug,
    eventCategory: marketData.eventCategory,
    source: source as Source,
  });
}

function SourceBadge({ source }: { source: "kalshi" | "polymarket" | "predict" }) {
  const cfg = SOURCE_CONFIG[source];
  if (!cfg) return null;

  return (
    <div className="w-5 h-5 rounded-[5px] flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color }}>
      {cfg.icon ? (
        <img src={cfg.icon} alt={cfg.label} className="w-3.5 h-3.5 object-contain" />
      ) : (
        <span className="text-[10px] font-black text-black leading-none">P</span>
      )}
    </div>
  );
}

function SourceLogoFull({ source }: { source: "kalshi" | "polymarket" | "predict" }) {
  if (source === "predict") return <img src="/logos/predictfun/Predict-Logo-Purp.svg" alt="Predict.fun" className="h-4 opacity-60 hover:opacity-100 transition-opacity" />;
  if (source === "kalshi") return <img src="/logos/kalshi/kalshi-logo.svg" alt="Kalshi" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity brightness-0 dark:brightness-100" />;
  return (
    <>
      <img src="/logos/polymarket/logo-black.svg" alt="Polymarket" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity dark:hidden" />
      <img src="/logos/polymarket/logo-white.svg" alt="Polymarket" className="h-3.5 opacity-50 hover:opacity-100 transition-opacity hidden dark:block" />
    </>
  );
}

function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return "Closed";

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h left`;
  }
  if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m left`;
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s left`;
  }
  return `${seconds}s left`;
}

export default function MarketHero({ marketData, source, isKalshi, umaDispute, showClosedBanner = true, hideStats = false }: MarketHeroProps) {
  const tr = useT();
  const router = useRouter();
  const isActive = marketData.acceptingOrders;
  const isMarketClosed = !marketData.acceptingOrders;
  const priceContext = useMarketPriceOptional();
  const [backLinkHovered, setBackLinkHovered] = useState(false);
  const [openInterest, setOpenInterest] = useState<number | null>(marketData.openInterest || null);

  // Fetch OI for Polymarket markets (lazy, client-side)
  useEffect(() => {
    if (isKalshi || !marketData.conditionId) return;
    fetch(`/api/markets/${marketData.conditionId}/oi`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.openInterest) setOpenInterest(d.openInterest); })
      .catch(() => {});
  }, [isKalshi, marketData.conditionId]);

  const yesPrice = priceContext?.buyPrices?.buyYes ?? marketData.outcomePrices[0] ?? 0.5;
  const noPrice = priceContext?.buyPrices?.buyNo ?? marketData.outcomePrices[1] ?? 0.5;

  const prevYesRef = useRef(yesPrice);
  const prevNoRef = useRef(noPrice);
  const [yesFlash, setYesFlash] = useState<"up" | "down" | null>(null);
  const [noFlash, setNoFlash] = useState<"up" | "down" | null>(null);

  // Countdown state (migrated from MarketStatusBanner)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isUrgent, setIsUrgent] = useState(false);

  useEffect(() => {
    if (yesPrice !== prevYesRef.current) {
      setYesFlash(yesPrice > prevYesRef.current ? "up" : "down");
      prevYesRef.current = yesPrice;
      const t = setTimeout(() => setYesFlash(null), 600);
      return () => clearTimeout(t);
    }
  }, [yesPrice]);

  useEffect(() => {
    if (noPrice !== prevNoRef.current) {
      setNoFlash(noPrice > prevNoRef.current ? "up" : "down");
      prevNoRef.current = noPrice;
      const t = setTimeout(() => setNoFlash(null), 600);
      return () => clearTimeout(t);
    }
  }, [noPrice]);

  // Countdown interval
  useEffect(() => {
    if (!marketData.endDate) return;

    const updateCountdown = () => {
      const now = Date.now();
      const end = new Date(marketData.endDate!).getTime();
      const remaining = end - now;

      setTimeRemaining(remaining);
      setIsUrgent(remaining > 0 && remaining < 3600000);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [marketData.endDate]);

  const formatVolume = (v: number) => {
    const n = v ?? 0;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  const outcome1Label = marketData.outcomes[0]?.toUpperCase() || "YES";
  const outcome2Label = marketData.outcomes[1]?.toUpperCase() || "NO";

  return (
    <div className="mb-0">

      {/* Title + image */}
      <div className="flex items-start gap-3 md:gap-4">
        {marketData.image && (
          <div className="relative w-[60px] h-[60px] md:w-[70px] md:h-[70px] flex-shrink-0 rounded-[5px] md:rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A]">
            <img
              src={marketData.image}
              alt={marketData.question}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).parentElement!.style.display = 'none'; }}
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          {marketData.slug && SOURCE_CONFIG[source] && (
            <a
              href={getExternalUrl(source, marketData)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="float-right ml-2 mt-0.5 opacity-70 hover:opacity-100 transition-opacity"
              title={`View on ${SOURCE_CONFIG[source].label}`}
            >
              <span className="md:hidden"><SourceBadge source={source} /></span>
              <span className="hidden md:inline-flex"><SourceLogoFull source={source} /></span>
            </a>
          )}
          {/* Breadcrumb — "View all outcomes" back-link for markets that
              belong to a multi-market event. Only rendered when the market
              has a parent event slug; hidden for standalone binary markets
              where this page IS the event view. */}
          {marketData.parentEventSlug && marketData.parentEventSlug !== marketData.slug && (
            <Link
              href={`/${source === "predict" ? "predictfun" : source}/event/${marketData.parentEventSlug}`}
              className="inline-flex items-center gap-1 mb-1.5 text-[11px] font-inter-tight font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors group"
            >
              <ChevronLeftIcon size={12} className="group-hover:-translate-x-0.5 transition-transform" />
              <span>{tr("common.viewAllOutcomes")}</span>
            </Link>
          )}
          {/* Line 1: Badges — solid bgs, matched weight with EventHero. */}
          <div className="flex items-center gap-2 mb-1">
            {marketData.closed && !marketData.acceptingOrders ? (
              <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-red-500 text-white">{tr("marketHero.resolved")}</span>
            ) : isActive ? (
              <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-emerald-500 text-white">{tr("marketHero.active")}</span>
            ) : (
              <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-red-500 text-white">{tr("hero.badge.closed")}</span>
            )}
            {marketData.eventCategory && (
              <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-inter-tight font-semibold uppercase tracking-wider bg-blue-600 text-white">
                {marketData.eventCategory}
              </span>
            )}
            {umaDispute && (
              <span className="inline-flex items-center h-[20px] px-2.5 rounded-[5px] text-[10px] leading-none font-bold font-inter-tight bg-amber-500 text-white">
                DISPUTED
              </span>
            )}
          </div>

          {/* Line 2: Title */}
          {(() => {
            const q = marketData.question || "";
            const upDownMatch = q.match(/^(.+?Up or Down)\s*-\s*(.+)$/i);
            const slug = marketData.slug || "";
            const intervalMatch = slug.match(/-(\d+m|\d+h)-/);
            const intervalLabel = intervalMatch
              ? { '5m': '5 Minutes', '15m': '15 Minutes', '1h': '1 Hour', '4h': '4 Hours' }[intervalMatch[1]] || null
              : null;

            if (upDownMatch && intervalLabel) {
              return (
                <>
                  <h1 className="text-base md:text-xl font-monument font-bold text-gray-900 dark:text-white leading-tight break-words">
                    {upDownMatch[1]} - {intervalLabel}
                  </h1>
                  <p className="font-satoshi text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {upDownMatch[2]}
                  </p>
                </>
              );
            }
            return (
              <h1 className="text-base md:text-xl font-monument font-bold text-gray-900 dark:text-white leading-tight break-words">
                {q}
              </h1>
            );
          })()}

          {/* Line 3: Stats — desktop */}
          {!hideStats && (
          <div className="hidden md:flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
              {formatVolume(marketData.volume)} Vol
            </span>
            {marketData.volume24hr > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                {formatVolume(marketData.volume24hr)} 24h
              </span>
            )}
            {marketData.liquidity > 0 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-[5px] text-[10px] font-inter-tight font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
                {formatVolume(marketData.liquidity)} Liq
              </span>
            )}
          </div>
          )}

        </div>
      </div>

      {/* Stats — mobile below image row (same hideStats gate as desktop) */}
      {!hideStats && (
      <div className="flex md:hidden flex-wrap items-center gap-1 mt-1.5">
        <span className="font-inter-tight px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
          {formatVolume(marketData.volume)} Vol
        </span>
        {marketData.volume24hr > 0 && (
          <span className="font-inter-tight px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
            {formatVolume(marketData.volume24hr)} 24h
          </span>
        )}
        {marketData.liquidity > 0 && (
          <span className="font-inter-tight px-1.5 py-0.5 rounded text-[9px] font-semibold bg-gray-100 dark:bg-[#111] border border-gray-200 dark:border-[#1A1A1A] text-gray-600 dark:text-gray-300">
            {formatVolume(marketData.liquidity)} Liq
          </span>
        )}
      </div>
      )}

      {/* Market closed banner — below image row */}
      {showClosedBanner && isMarketClosed && (
        <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-[5px] bg-red-500/5 border border-red-500/10">
          <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
          <span className="font-satoshi text-sm font-bold text-red-500">
            Market closed — no longer accepting trades
          </span>
        </div>
      )}
    </div>
  );
}
